/**
 * Reference CSV for item master bulk upload fields.
 *
 * One row per upload column. Field Name uses the same labels and required markers
 * (* = required, (*) = conditionally required) as the upload template.
 *
 * Accepted Values uses semicolon-separated entries. Leave blank when values are
 * free-form or defined by constraints instead.
 */

import { ITEM_MASTER_COLUMN_DEFS } from './itemMasterBulkUploadColumns';

const FIELD_REFERENCE_FILENAME = 'item_master_bulk_upload_field_reference.csv';

const FIELD_REFERENCE_COLUMN_DEFS = [
    { key: 'fieldName', label: 'Field Name' },
    { key: 'dataType', label: 'Data Type' },
    { key: 'acceptedValues', label: 'Accepted Values' },
    { key: 'constraints', label: 'Constraints' },
    { key: 'notes', label: 'Notes' },
];

const BOOLEAN_VALUES = 'yes; no; true; false';
const UOM_VALUES = 'oz; ct; lb; floz; pt; g';

/** Schema metadata keyed by column key; order comes from ITEM_MASTER_COLUMN_DEFS. */
const FIELD_METADATA = {
    action: {
        dataType: 'enum',
        acceptedValues: 'add; update; delete',
        constraints: '',
        notes: 'Required on every row.',
    },
    externalSku: {
        dataType: 'integer',
        acceptedValues: '',
        constraints: 'max: 199999',
        notes: 'Required for update when Internal Name is blank. Must reference an active item master.',
    },
    internalName: {
        dataType: 'text',
        acceptedValues: '',
        constraints: '',
        notes: 'Required for add. For update, required when External SKU is blank. Must reference an active item master.',
    },
    companySources: {
        dataType: 'list',
        acceptedValues: 'misfits_market; blue_apron; marley_spoon',
        constraints: '',
        notes: 'Comma-separated company source codes.',
    },
    brand: {
        dataType: 'text',
        acceptedValues: '',
        constraints: '',
        notes: 'Required for add, except when taxonomy resolves to Produce L1.',
    },
    basePrice: {
        dataType: 'decimal',
        acceptedValues: '',
        constraints: 'min: 0.01; max: 300',
        notes: 'Required for add.',
    },
    categoryOne: {
        dataType: 'taxonomy',
        acceptedValues: '',
        constraints: 'level: 1',
        notes: 'Must match an active level-1 taxonomy name. Auto-populated when a deeper-level category is provided.',
    },
    categoryTwo: {
        dataType: 'taxonomy',
        acceptedValues: '',
        constraints: 'level: 2',
        notes: 'Must match an active level-2 taxonomy name. Auto-populated when a deeper-level category is provided.',
    },
    categoryThree: {
        dataType: 'taxonomy',
        acceptedValues: '',
        constraints: 'level: 3',
        notes: 'Required for add, except when taxonomy resolves to Produce L1. Must match an active level-3 taxonomy name. Auto-populated when a deeper-level category is provided.',
    },
    categoryFour: {
        dataType: 'taxonomy',
        acceptedValues: '',
        constraints: 'level: 4',
        notes: 'Required for add when taxonomy resolves to Produce L1. Must match an active level-4 taxonomy name.',
    },
    sellableUnitsPerCase: {
        dataType: 'integer',
        acceptedValues: '',
        constraints: 'min: 1',
        notes: 'Required for add.',
    },
    purchasingCaseUOM: {
        dataType: 'enum',
        acceptedValues: UOM_VALUES,
        constraints: '',
        notes: 'Required for add.',
    },
    casesPerPallet: {
        dataType: 'integer',
        acceptedValues: '',
        constraints: 'min: 1',
        notes: 'Required for add.',
    },
    poundsPerCase: {
        dataType: 'decimal',
        acceptedValues: '',
        constraints: 'min: 0.01',
        notes: 'Required for add.',
    },
    portionCount: {
        dataType: 'integer',
        acceptedValues: '',
        constraints: 'min: 1; max: 40',
        notes: 'Required for add.',
    },
    replenishment: {
        dataType: 'list',
        acceptedValues: '',
        constraints: '',
        notes: 'Comma-separated facility names. Each name must match an existing facility.',
    },
    hi: {
        dataType: 'integer',
        acceptedValues: '',
        constraints: 'min: 1',
        notes: 'Required for add.',
    },
    ti: {
        dataType: 'integer',
        acceptedValues: '',
        constraints: 'min: 1',
        notes: 'Required for add.',
    },
    vendorGuaranteedDays: {
        dataType: 'integer',
        acceptedValues: '',
        constraints: 'min: 1',
        notes: 'Required for add.',
    },
    unitLengthIn: {
        dataType: 'decimal',
        acceptedValues: '',
        constraints: 'min: 0.01',
        notes: 'Required for add.',
    },
    unitWidthIn: {
        dataType: 'decimal',
        acceptedValues: '',
        constraints: 'min: 0.01',
        notes: 'Required for add.',
    },
    unitHeightIn: {
        dataType: 'decimal',
        acceptedValues: '',
        constraints: 'min: 0.01',
        notes: 'Required for add.',
    },
    caseLengthIn: {
        dataType: 'decimal',
        acceptedValues: '',
        constraints: 'min: 0.01',
        notes: 'Required for add.',
    },
    caseWidthIn: {
        dataType: 'decimal',
        acceptedValues: '',
        constraints: 'min: 0.01',
        notes: 'Required for add.',
    },
    caseHeightIn: {
        dataType: 'decimal',
        acceptedValues: '',
        constraints: 'min: 0.01',
        notes: 'Required for add.',
    },
    receivingTempZone: {
        dataType: 'enum',
        acceptedValues: 'Ambient; Chilled; Frozen',
        constraints: '',
        notes: 'Required for add.',
    },
    storageTempZone: {
        dataType: 'enum',
        acceptedValues: 'Ambient (>55); Chilled (32-35); Frozen (0-31); Nightshade (50-55); Refrigerated (36-40)',
        constraints: '',
        notes: 'Required for add.',
    },
    carrierShippingTempZone: {
        dataType: 'enum',
        acceptedValues: 'Ambient; Refrigerated; Cold Keeper',
        constraints: '',
        notes: 'Required for add.',
    },
    dbiShippingTempZone: {
        dataType: 'enum',
        acceptedValues: 'Ambient; Cold Keeper; Frozen',
        constraints: '',
        notes: 'Required for add.',
    },
    inboundShippingTempZone: {
        dataType: 'enum',
        acceptedValues: '32-36(Cold); 38-42(Cold); 40-45(Warm); 45-50(Warm); 60-65(Warm)',
        constraints: '',
        notes: 'Can be cleared on update with null.',
    },
    sellableUnitUOM: {
        dataType: 'enum',
        acceptedValues: UOM_VALUES,
        constraints: '',
        notes: 'Required for add.',
    },
    sellableUnitSize: {
        dataType: 'decimal',
        acceptedValues: '',
        constraints: 'min: 0.01',
        notes: 'Required for add.',
    },
    skuDateType: {
        dataType: 'enum',
        acceptedValues: 'NO_EXPIRATION_DATE; EXPIRATION_DATE; PRODUCTION_DATE; DAYS_AFTER_RECEIPT',
        constraints: '',
        notes: 'Required for add.',
    },
    cubeLevelPerSellableUnit: {
        dataType: 'integer',
        acceptedValues: '',
        constraints: 'min: 1; max: 100',
        notes: 'Required for add.',
    },
    customerGuaranteeDays: {
        dataType: 'integer',
        acceptedValues: '',
        constraints: '',
        notes: 'Required for add.',
    },
    unitWeightOz: {
        dataType: 'decimal',
        acceptedValues: '',
        constraints: 'min: 0.01',
        notes: 'Can be cleared on update with null.',
    },
    barcode: {
        dataType: 'integer',
        acceptedValues: '',
        constraints: '',
        notes: 'Can be cleared on update with null.',
    },
    barcodeType: {
        dataType: 'enum',
        acceptedValues: 'UPC; PLU; EAN',
        constraints: '',
        notes: 'Can be cleared on update with null.',
    },
    ogCv: {
        dataType: 'enum',
        acceptedValues: 'ORGANIC; CONVENTIONAL',
        constraints: '',
        notes: 'Required for add when taxonomy resolves to Produce L1.',
    },
    prop65Warning: {
        dataType: 'boolean',
        acceptedValues: BOOLEAN_VALUES,
        constraints: '',
        notes: '',
    },
    prop65Alcohol: {
        dataType: 'boolean',
        acceptedValues: BOOLEAN_VALUES,
        constraints: '',
        notes: '',
    },
    prop65Canned: {
        dataType: 'boolean',
        acceptedValues: BOOLEAN_VALUES,
        constraints: '',
        notes: '',
    },
    prop65Food: {
        dataType: 'boolean',
        acceptedValues: BOOLEAN_VALUES,
        constraints: '',
        notes: '',
    },
    prop65NonFood: {
        dataType: 'boolean',
        acceptedValues: BOOLEAN_VALUES,
        constraints: '',
        notes: '',
    },
    prop65Ingredient: {
        dataType: 'text',
        acceptedValues: '',
        constraints: '',
        notes: 'Must match an existing Prop65 ingredient name.',
    },
    sellOnPoQty: {
        dataType: 'boolean',
        acceptedValues: BOOLEAN_VALUES,
        constraints: '',
        notes: '',
    },
    expireLifeDays: {
        dataType: 'integer',
        acceptedValues: '',
        constraints: '',
        notes: '',
    },
    dbiOnly: {
        dataType: 'boolean',
        acceptedValues: BOOLEAN_VALUES,
        constraints: '',
        notes: '',
    },
    purchasingCasePackSize: {
        dataType: 'integer',
        acceptedValues: '',
        constraints: 'min: 1',
        notes: 'Required for add.',
    },
    packagingUnitSize: {
        dataType: 'integer',
        acceptedValues: '',
        constraints: 'min: 1',
        notes: 'Must be provided together with Packaging Unit Type. Can be cleared on update with null.',
    },
    packagingUnitType: {
        dataType: 'enum',
        acceptedValues: 'clamshell; pack; head; bag; bunch',
        constraints: '',
        notes: 'Must be provided together with Packaging Unit Size. Can be cleared on update with null.',
    },
    mpIndividualUnitSize: {
        dataType: 'decimal',
        acceptedValues: '',
        constraints: 'min: 0.01',
        notes: 'Must be provided together with MP Individual Unit UOM. Can be cleared on update with null.',
    },
    mpIndividualUOM: {
        dataType: 'enum',
        acceptedValues: 'oz; floz',
        constraints: '',
        notes: 'Must be provided together with MP Individual Unit Size. Can be cleared on update with null.',
    },
    isBulkItem: {
        dataType: 'boolean',
        acceptedValues: BOOLEAN_VALUES,
        constraints: '',
        notes: '',
    },
    tier: {
        dataType: 'enum',
        acceptedValues: 'A; B; C',
        constraints: '',
        notes: 'Should only be supplied if Company Sources includes misfits_market.',
    },
};

function formatFieldName({ conditionallyRequired, label, required }) {
    if (required) {
        return `${label}*`;
    }
    if (conditionallyRequired) {
        return `${label}(*)`;
    }
    return label;
}

function buildFieldReferenceRows() {
    return ITEM_MASTER_COLUMN_DEFS.map((column) => {
        const meta = FIELD_METADATA[column.key];
        if (!meta) {
            throw new Error(`Missing field reference metadata for "${column.key}"`);
        }

        return {
            fieldName: formatFieldName(column),
            ...meta,
        };
    });
}

function buildCsvHeaders(columnDefs) {
    return columnDefs.map(({ label }) => label).join(',');
}

function formatCsvRow(columnDefs, row) {
    return columnDefs
        .map((column) => {
            const value = String(row[column.key] ?? '');
            if (value.includes(',') || value.includes('"') || value.includes('\n')) {
                return `"${value.replace(/"/g, '""')}"`;
            }
            return value;
        })
        .join(',');
}

export function downloadItemMasterBulkUploadFieldReference() {
    const headers = buildCsvHeaders(FIELD_REFERENCE_COLUMN_DEFS);
    const dataRows = buildFieldReferenceRows().map((row) => formatCsvRow(FIELD_REFERENCE_COLUMN_DEFS, row));
    const csvContent = [headers, ...dataRows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = FIELD_REFERENCE_FILENAME;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}
